import { useState } from "react";

// Third Party
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

//helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

// import {
// 	Select,
// 	SelectContent,
// 	SelectItem,
// 	SelectTrigger,
// 	SelectValue,
// } from "@/components/ui/select";
// import { Label } from "@/components/ui/label";
import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";

// Services
import { getActionPlanDetails } from "@/services/action-plan";

// Icons
import { ArrowLeft, Eye } from "lucide-react";

// Utils
import { formatNumber } from "@/utils/formatNumber";

export const Route = createFileRoute("/_layout-private/report/action-plan/$id")(
  {
    component: RouteComponent,
  }
);

function RouteComponent() {
  const [unit, setUnit] = useState("1");
  const navigate = useNavigate();
  const id = localStorage.getItem("companyId");
  const yearly = Number(localStorage.getItem("year"));
  const { operationPlan } = useAccessPermission();

  const {
    isLoading,
    isError,
    data: actionPlanDetails,
    error,
  } = useQuery({
    queryKey: ["actionPlanDetails"],
    queryFn: () => getActionPlanDetails(id as string, Number(yearly)),
  });

  if (isLoading) {
    return <DinamixSkeletonTable />;
  }
  if (isError) {
    return <p>error {error.message}</p>;
  }
  const transformData = (data: any) => {
    const currentPlanDetails =
      actionPlanDetails.data.finInIncome.currentPlan.details;
    const currentExecutionDetails =
      actionPlanDetails.data.finInIncome.currentExecution.details;
    const lastExecutionDetails =
      actionPlanDetails.data.finInIncome.lastExecution.details;
    const finInExpenseCurrentPlanDetails =
      actionPlanDetails.data.finInExpense.currentPlan.details;
    const finInExpenseCurrentExecutionDetails =
      actionPlanDetails.data.finInExpense.currentExecution.details;
    const finInExpenseLastExecutionDetails =
      actionPlanDetails.data.finInExpense.lastExecution.details;

    const maxLength = Math.max(
      currentPlanDetails.length,
      currentExecutionDetails.length,
      lastExecutionDetails.length
    );
    const maxLengthFinInExpense = Math.max(
      finInExpenseCurrentPlanDetails.length,
      finInExpenseCurrentExecutionDetails.length,
      finInExpenseLastExecutionDetails.length
    );

    const details = Array.from({ length: maxLength }).map((_, index) => ({
      currentPlanDetailName: currentPlanDetails[index]?.detailName || "-",
      currentPlanAmount: currentPlanDetails[index]?.amount || "-",
      currentExecutionDetailName:
        currentExecutionDetails[index]?.detailName || "-",
      currentExecutionAmount: currentExecutionDetails[index]?.amount || "-",
      lastExecutionDetailsDetailName:
        lastExecutionDetails[index]?.detailName || "-",
      lastExecutionAmount: lastExecutionDetails[index]?.amount || "-",
    }));

    const finInExpenseDetails = Array.from({
      length: maxLengthFinInExpense,
    }).map((_, index) => ({
      currentPlanDetailName: currentPlanDetails[index]?.detailName || "-",
      currentPlanAmount: currentPlanDetails[index]?.amount || "-",
      currentExecutionDetailName:
        currentExecutionDetails[index]?.detailName || "-",
      currentExecutionAmount: currentExecutionDetails[index]?.amount || "-",
      lastExecutionDetailsDetailName:
        lastExecutionDetails[index]?.detailName || "-",
      lastExecutionAmount: lastExecutionDetails[index]?.amount || "-",
    }));

    return {
      data: {
        finInIncome: {
          "currentPlan-total":
            actionPlanDetails.data.finInIncome.currentPlan.total,
          "currentExecution-total":
            actionPlanDetails.data.finInIncome.currentExecution.total,
          details,
        },

        finInExpense: {
          "currentPlan-total":
            actionPlanDetails.data.finInIncome.currentPlan.total,
          "currentExecution-total":
            actionPlanDetails.data.finInIncome.currentExecution.total,
          finInExpenseDetails,
        },
      },
    };
  };

  // console.log("dataDetailsnoyes,", actionPlanDetails.data);

  const data = transformData(actionPlanDetails.data);
  const finInIncome = actionPlanDetails.data.finInIncome;
  const finInExpense = actionPlanDetails.data.finInExpense;
  const finProfit = actionPlanDetails.data.finProfit;

  console.log("Plan", data);

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link replace to="/report/action-plan" search={{ page: 1, startYear: 0, endYear: 0 }}>
                ລາຍງານແຜນດຳເນີນງານ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍລະອຽດ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/action-plan",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-1 mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" /> ກັບຄືນ
          </button>

          <CardTitle>
            {/* {actionPlanDetails?.data?.company?.companyName} */}
            ລາຍລະອຽດແຜນດຳເນີນງານ
          </CardTitle>
          <CardDescription>
            ໃນປີ {yearly}, {actionPlanDetails?.data?.company?.companyName}{" "}
            ມີລາຍຮັບ, ລາຍຈ່າຍ ແລະ ກຳໄລ(ຂາດທຶນ) ລາຍລະອຽດສະແດງໃນຕາຕະລາງລຸ່ມນີ້:
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-2 gap-x-2">
            {/* <div className="flex items-center gap-x-1">
							<Label htmlFor="">ຫົວໜ່ວຍເງິນ</Label>
							<Select
								onValueChange={(value) => setUnit(value)}
								defaultValue="1"
							>
								<SelectTrigger className="w-[120px] indent-1	">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="1">1</SelectItem>
									<SelectItem value="10">10</SelectItem>
									<SelectItem value="100">100</SelectItem>
									<SelectItem value="1000">1000</SelectItem>
									<SelectItem value="10000">10000</SelectItem>
									<SelectItem value="100000">100000</SelectItem>
									<SelectItem value="1000000">1000000</SelectItem>
								</SelectContent>
							</Select>
						</div> */}
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <Table>
            <TableHeader>
              <TableRow className="[&>*]:whitespace-nowrap [&>*]:border [&>*]:font-bold [&>*]:py-5 [&>*]:text-center [&>*]:text-black [&>*]:bg-gray-100 border[&>*]:border-gray-300">
                <TableHead className="rounded-tl-lg">ລຳດັບ</TableHead>
                <TableHead>ເນື້ອໃນ</TableHead>
                <TableHead>ຕົວຈິງ {yearly}</TableHead>
                <TableHead>ແຜນການ {yearly}</TableHead>
                <TableHead>ຈໍານວນປ່ຽນແປງ</TableHead>
                <TableHead>ທຽບໃສ່ແຜນ {yearly - 1}</TableHead>
                <TableHead>ຕົວຈິງ {yearly - 1}</TableHead>
                <TableHead>ຈຳນວນປ່ຽນແປງ</TableHead>
                <TableHead className="rounded-tr-lg">
                  ທຽບໃສ່ {yearly - 1}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="font-bold">
                <TableCell className="border">{"1"}</TableCell>
                <TableCell className="border whitespace-nowrap">
                  {"ລາຍຮັບ"}
                </TableCell>
                <TableCell
                  className="text-right text-blue-600 underline border cursor-pointer gap-x-2"
                  onClick={() => {
                    localStorage.setItem("year", yearly.toString());
                    navigate({
                      to: "/report/finance/company/result",
                      search: {
                        refId: actionPlanDetails?.data?.id,
                      },
                    });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Eye size={16} />
                    {formatNumber(
                      finInIncome.currentExecution.total / Number(unit)
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className="text-right text-blue-600 underline border cursor-pointer gap-x-2"
                  onClick={() => {
                    navigate({
                      to: "/report/action-plan/preview",
                      search: {
                        yearly: yearly.toString(),
                      },
                    });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Eye size={16} />{" "}
                    {formatNumber(
                      finInIncome?.currentPlan?.total / Number(unit)
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right border">
                  {formatNumber(
                    finInIncome?.currentDiffs?.total / Number(unit)
                  )}
                </TableCell>
                <TableCell className="text-right border">
                  {finInIncome?.currentPctDiffs?.total?.toFixed(2)} %
                </TableCell>
                <TableCell
                  className="text-right text-blue-600 underline border cursor-pointer"
                  onClick={() => {
                    localStorage.setItem("year", (yearly - 1).toString());
                    navigate({
                      to: "/report/finance/company/result",
                      search: {
                        refId: actionPlanDetails?.data?.id,
                      },
                    });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Eye size={16} />
                    {formatNumber(
                      finInIncome?.lastExecution?.total / Number(unit)
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right border">
                  {(
                    finInIncome?.lastDiffs?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {finInIncome?.lastPctDiffs?.total?.toFixed(2)} %
                </TableCell>
              </TableRow>

              <TableRow className="font-bold">
                <TableCell className="border">{"2"}</TableCell>
                <TableCell className="border">{"ລາຍຈ່າຍ"}</TableCell>
                <TableCell
                  className="text-right text-blue-600 underline border cursor-pointer"
                  onClick={() => {
                    localStorage.setItem("year", yearly.toString());
                    navigate({
                      to: "/report/finance/company/result",
                      search: {
                        refId: actionPlanDetails?.data?.id,
                      },
                    });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Eye size={16} />
                    {(
                      finInExpense.currentExecution.total / Number(unit)
                    ).toLocaleString()}
                  </div>
                </TableCell>
                <TableCell
                  className="text-right text-blue-600 underline border cursor-pointer"
                  onClick={() => {
                    navigate({
                      to: "/report/action-plan/preview",
                      search: {
                        yearly: yearly.toString(),
                      },
                    });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Eye size={16} />
                    {(
                      finInExpense?.currentPlan?.total / Number(unit)
                    ).toLocaleString()}
                  </div>
                </TableCell>
                <TableCell className="text-right border">
                  {(
                    finInExpense?.currentDiffs?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {finInExpense?.currentPctDiffs?.total?.toFixed(2)} %
                </TableCell>
                <TableCell
                  className="text-right text-blue-600 underline border cursor-pointer"
                  onClick={() => {
                    localStorage.setItem("year", (yearly - 1).toString());
                    navigate({
                      to: "/report/finance/company/result",
                      search: {
                        refId: actionPlanDetails?.data?.id,
                      },
                    });
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Eye size={16} />
                    {(
                      finInExpense?.lastExecution?.total / Number(unit)
                    ).toLocaleString()}
                  </div>
                </TableCell>
                <TableCell className="text-right border">
                  {(
                    finInExpense?.lastDiffs?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {finInExpense?.lastPctDiffs?.total?.toFixed(2)} %
                </TableCell>
              </TableRow>

              <TableRow className="font-bold bg-gray-100">
                <TableCell className="border"> </TableCell>
                <TableCell className="border">{"ກຳໄລ/(ຂາດທຶນ)ສຸດທິ"}</TableCell>
                <TableCell className="text-right border">
                  {(
                    finProfit?.currentExecution?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {(
                    finProfit?.currentPlan?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {(
                    finProfit?.currentDiffs?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {finProfit?.currentPctDiffs?.total?.toFixed(2)} %
                </TableCell>
                <TableCell className="text-right border">
                  {(
                    finProfit?.lastExecution?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {(
                    finProfit?.lastDiffs?.total / Number(unit)
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right border">
                  {finProfit?.lastPctDiffs?.total?.toFixed(2)} %
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {actionPlanDetails?.data?.docFile
            ? operationPlan.isDownload && (
              <div className="mt-2">
                <FileHandleViewer
                  fileUrl={actionPlanDetails?.data?.docFile}
                />
              </div>
            )
            : null}
        </CardContent>
      </Card>
    </>
  );
}
